import React, { useEffect, useState } from "react";
import { Box, Flex, Icon, Pagination, Spinner, Text } from "@telia/teddy";
import { Link } from "react-router-dom";
import { retailerFetch } from "../../retailerFetch";
import PAGES from "../../../../../constants/pages";
import "./RecentActivity.less";

const PAGE_SIZE = 20;

type ActivityItem = {
    type: "comment" | "resolved";
    timestamp: string;
    actorName: string;
    feedbackOwnerName: string | null;
    feedbackId: string;
};

const formatRelativeTime = (isoDate: string): string => {
    const diff = Date.now() - new Date(isoDate).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Nå";
    if (minutes < 60) return `${minutes} min siden`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${hours === 1 ? "time" : "timer"} siden`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "I går";
    return `${days} dager siden`;
};

const ActivityCard: React.FC<{ item: ActivityItem }> = ({ item }) => {
    const firstName = item.actorName.split(" ")[0];
    const ownerFirst = item.feedbackOwnerName?.split(" ")[0];
    const feedbackLink = `${PAGES.RETAILER.FEEDBACK_DETAILS.URL}${item.feedbackId}`;
    const isSelf = ownerFirst && firstName.toLowerCase() === ownerFirst.toLowerCase();

    return (
        <Box className={`recent-activity-card recent-activity-card--${item.type}`}>
            <Flex gap="200" align="center">
                <Box className={`recent-activity-card__icon recent-activity-card__icon--${item.type}`}>
                    <Icon name={item.type === "resolved" ? "check-circle" : "chat"} size="sm" aria-hidden="true" />
                </Box>
                <Flex direction="column" gap="050">
                    <Text variant="paragraph-100" className="recent-activity-card__text">
                        {item.type === "resolved" ? (
                            <>
                                <strong>{firstName}</strong> sin{" "}
                                <Link to={feedbackLink} className="recent-activity-card__link">
                                    tilbakemelding
                                </Link>{" "}
                                ble markert som løst
                            </>
                        ) : isSelf ? (
                            <>
                                <strong>{firstName}</strong> kommenterte i{" "}
                                <Link to={feedbackLink} className="recent-activity-card__link">
                                    sin egen tilbakemelding
                                </Link>
                            </>
                        ) : (
                            <>
                                <strong>{firstName}</strong> kommenterte i {ownerFirst ?? "en"} sin{" "}
                                <Link to={feedbackLink} className="recent-activity-card__link">
                                    tilbakemelding
                                </Link>
                            </>
                        )}
                    </Text>
                    <Text variant="additional-100" className="recent-activity-card__time">
                        {formatRelativeTime(item.timestamp)}
                    </Text>
                </Flex>
            </Flex>
        </Box>
    );
};

const RecentActivity: React.FC = () => {
    const [activity, setActivity] = useState<ActivityItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const fetchActivity = async () => {
            try {
                const res = await retailerFetch("/shop/api/superadmin/feedback/activity");
                if (res.ok) {
                    setActivity(await res.json());
                }
            } finally {
                setLoading(false);
            }
        };
        fetchActivity();
    }, []);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(activity.length / PAGE_SIZE));
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [activity, currentPage]);

    if (loading) return <Spinner size="sm" />;
    if (activity.length === 0) return <Text>Ingen nylig aktivitet.</Text>;

    const totalPages = Math.ceil(activity.length / PAGE_SIZE);
    const pageItems = activity.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <Flex direction="column" gap="200" className="recent-activity">
            {pageItems.map((item) => (
                <ActivityCard key={`${item.feedbackId}-${item.timestamp}`} item={item} />
            ))}
            {totalPages > 1 && (
                <Pagination aria-label="Aktivitetsnavigasjon" className="recent-activity__pagination">
                    <Pagination.Navigation
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        side="prev"
                        showLabels={false}
                    />
                    <Pagination.PageNumbers
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        maxVisiblePages={5}
                    />
                    <Pagination.Navigation
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        side="next"
                        showLabels={false}
                    />
                </Pagination>
            )}
        </Flex>
    );
};

export default RecentActivity;
